import React, { useEffect, useRef, useState } from "react";
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@material-ui/core";
import PhoneIcon from "@material-ui/icons/Phone";
import api from "../../services/api";

const time = seconds => `${Math.floor(seconds / 60).toString().padStart(2, "0")}:${Math.floor(seconds % 60).toString().padStart(2, "0")}`;
const errors = {
  OPENAI_KEY_MISSING: "A transcrição ainda não está configurada.",
  OPENAI_HTTP_401: "Não foi possível autenticar o serviço de transcrição.",
  OPENAI_HTTP_429: "O serviço atingiu o limite de uso. Tente novamente mais tarde.",
  AUDIO_TOO_LARGE: "A gravação excedeu o tamanho permitido para transcrição.",
};

export default function CallLog({ message, compact = false }) {
  const [open, setOpen] = useState(false);
  const [call, setCall] = useState(null);
  const [error, setError] = useState("");
  const [retrying, setRetrying] = useState(false);
  const [audioUrl, setAudioUrl] = useState("");
  const [loadingAudio, setLoadingAudio] = useState(false);
  const audioRef = useRef("");
  const generation = useRef(0);
  const callId = message.id.replace(/^platform-call-/, "");
  const endpoint = `/tickets/${message.ticketId}/calls/${callId}`;

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    let timer;
    const load = async () => {
      try {
        const { data } = await api.get(endpoint);
        if (!active) return;
        setCall(data);
        setError("");
        if (["pending", "queued", "processing"].includes(data.transcriptionStatus)) timer = setTimeout(load, 4000);
      } catch (_) { if (active) setError("Não foi possível consultar esta ligação. Verifique seu acesso e tente novamente."); }
    };
    load();
    return () => { active = false; clearTimeout(timer); };
  }, [open, endpoint, retrying]);

  useEffect(() => () => {
    generation.current += 1;
    if (audioRef.current) URL.revokeObjectURL(audioRef.current);
  }, []);

  const close = () => {
    generation.current += 1;
    setOpen(false);
    setCall(null);
    setError("");
    setLoadingAudio(false);
    if (audioRef.current) URL.revokeObjectURL(audioRef.current);
    audioRef.current = "";
    setAudioUrl("");
  };
  const loadAudio = async () => {
    const current = generation.current;
    setLoadingAudio(true);
    try {
      const { data } = await api.get(`${endpoint}/recording`, { responseType: "blob" });
      if (current !== generation.current) return;
      if (audioRef.current) URL.revokeObjectURL(audioRef.current);
      audioRef.current = URL.createObjectURL(data);
      setAudioUrl(audioRef.current);
    } catch (_) { if (current === generation.current) setError("Não foi possível carregar a gravação."); }
    finally { if (current === generation.current) setLoadingAudio(false); }
  };
  const retry = async () => {
    setRetrying(true);
    try {
      await api.post(`${endpoint}/transcription/retry`);
      setCall(previous => ({ ...previous, transcriptionStatus: "queued" }));
      setError("");
    } catch (_) { setError("Não foi possível solicitar uma nova tentativa."); }
    finally { setRetrying(false); }
  };

  return (
    <>
      {!compact && <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 4px" }}>
        <PhoneIcon fontSize="small" color="primary" />
        <span>{message.body}</span>
      </div>}
      <div style={{ padding: "0 4px 4px" }}>
        <Button size="small" color="primary" onClick={() => setOpen(true)}>Ver transcrição/gravação</Button>
      </div>
      <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
        <DialogTitle>Gravação e transcrição da ligação</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" gutterBottom>{message.body}</Typography>
          {error && <Typography color="error" role="alert">{error}</Typography>}
          {!call && !error && <CircularProgress size={24} aria-label="Carregando ligação" />}
          {call && <>
            {call.recordingStatus === "ready" && !audioUrl && <Button disabled={loadingAudio} onClick={loadAudio} color="primary">
              {loadingAudio ? "Carregando gravação…" : "Ouvir gravação"}
            </Button>}
            {audioUrl && <audio controls src={audioUrl} style={{ width: "100%", margin: "12px 0" }} />}
            {call.recordingStatus === "processing" && <Typography variant="body2">Preparando gravação…</Typography>}
            {call.recordingStatus === "failed" && <Typography color="error">Não foi possível concluir a gravação desta ligação.</Typography>}
            {call.transcriptionStatus === "pending" && <Typography>A transcrição ficará disponível após o encerramento da ligação.</Typography>}
            {["queued", "processing"].includes(call.transcriptionStatus) && <Typography role="status">Processando transcrição…</Typography>}
            {call.transcriptionStatus === "unavailable" && <Typography>Não há gravação disponível para transcrever esta ligação.</Typography>}
            {call.transcriptionStatus === "failed" && <>
              <Typography color="error">{errors[call.transcriptionError] || "Não foi possível concluir a transcrição."}</Typography>
              {call.recordingStatus === "ready" && <Button onClick={retry} disabled={retrying} color="primary">Tentar transcrição novamente</Button>}
            </>}
            {call.transcriptionStatus === "ready" && <>
              <Typography variant="caption" color="textSecondary">Transcrição automática; pode conter imprecisões.</Typography>
              {call.segments.length === 0 && <Typography>Nenhuma fala foi identificada.</Typography>}
              {call.segments.map((segment, index) => <div key={index} style={{ marginTop: 12 }}>
                <Typography variant="caption" color="textSecondary">{time(segment.start)} · {segment.speaker === "Atendente" ? call.userName || "Atendente" : "Cliente"}</Typography>
                <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>{segment.text}</Typography>
              </div>)}
            </>}
          </>}
        </DialogContent>
        <DialogActions><Button onClick={close}>Fechar</Button></DialogActions>
      </Dialog>
    </>
  );
}
